import React, { FC, Fragment } from 'react';
import { graphql, createFragmentContainer } from 'react-relay';
import { FormattedMessage } from 'react-intl';
import tracking from 'dibs-tracking';
import { SeoLink } from 'dibs-seo/exports/SeoLink';

import HpLazyImageWrapper from '../HpLazyImageWrapper/HpLazyImageWrapper';
import { HpRespCarousel, handleItemClick } from '../HpRespCarousel/HpRespCarousel';
import ResponsiveImage from '../ResponsiveImage';
import { HpRespLocationItems_viewer } from './__generated__/HpRespLocationItems_viewer.graphql';

import styles from './HpRespLocationItems.scss';

const SHOP_LOCAL_ACTION_LABEL = 'shop local';

type Props = {
    viewer: HpRespLocationItems_viewer;
    itemCount: number;
    useLazyLoadImages: boolean;
};

export const HpRespLocationItemsComponent: FC<Props> = ({
    viewer,
    itemCount,
    useLazyLoadImages,
}) => {
    const { displayName, linkData: headerLinkData, items = [] } =
        viewer?.locationItemsInfo?.[0]?.locationPage || {};
    if (!displayName || !headerLinkData || !items || items.length < itemCount) {
        return null;
    }
    const title = (
        <FormattedMessage
            id="homepage.modules.locationItems.title"
            defaultMessage="Shop {displayName} Sellers"
            values={{ displayName }}
        />
    );

    return (
        <Fragment>
            <div className={styles.headerContainer}>
                <div className={styles.title}>{title}</div>
                <SeoLink
                    className={styles.headerCta}
                    dataTn="HpRespLocationItems-headerCta"
                    linkData={headerLinkData}
                    onClick={() =>
                        tracking.trackEvent({
                            category: 'promo interaction',
                            action: `Shop Local view all clicked`,
                            label: '[homepage]',
                        })
                    }
                >
                    <FormattedMessage
                        id="homepage.modules.locationItems.viewMoreCta"
                        defaultMessage="View All"
                    />
                </SeoLink>
            </div>
            <HpRespCarousel actionLabel={SHOP_LOCAL_ACTION_LABEL} items={items}>
                {items.slice(0, itemCount).map((item, index) => {
                    if (!item) {
                        return null;
                    }
                    const { serviceId, linkData } = item;
                    if (!serviceId || !linkData) {
                        return null;
                    }

                    return (
                        <div className={styles.itemWrapper} key={serviceId}>
                            <SeoLink
                                className={styles.headerCta}
                                dataTn={`HpRespLocationItems-${serviceId}`}
                                linkData={linkData}
                                onClick={() =>
                                    handleItemClick({
                                        item,
                                        index,
                                        actionLabel: SHOP_LOCAL_ACTION_LABEL,
                                    })
                                }
                            >
                                {item.firstPhotoWebPath && (
                                    <HpLazyImageWrapper useLazyLoad={useLazyLoadImages}>
                                        <ResponsiveImage
                                            className={styles.image}
                                            src={item.firstPhotoWebPath}
                                            srcSetWidths={[150, 240]}
                                        />
                                    </HpLazyImageWrapper>
                                )}
                            </SeoLink>
                        </div>
                    );
                })}
            </HpRespCarousel>
        </Fragment>
    );
};

export const HpRespLocationItems = createFragmentContainer(HpRespLocationItemsComponent, {
    viewer: graphql`
        fragment HpRespLocationItems_viewer on Viewer {
            locationItemsInfo: regionalInfo(regionTypes: "ITEM_LOCATION", zipCode: $zipCode)
                @include(if: $isClient) {
                locationPage(
                    minCount: 1
                    maxWeight: METROPOLITAN
                    minWeight: DISTRICT
                    usOnly: true
                ) {
                    displayName
                    linkData {
                        ...SeoLink_linkData
                    }
                    items(count: $locationItemsCount) {
                        browseUrl
                        categoryCode
                        serviceId
                        contemporaryTrackingString
                        firstPhotoWebPath(size: master)
                        linkData {
                            ...SeoLink_linkData
                        }
                        seller {
                            serviceId
                        }
                        title
                    }
                }
            }
        }
    `,
});
